import { Flex, Text, Tooltip } from "@powerledger/ui-component-lib";
import { TFunction } from "i18next";
import { startCase, toLower } from "lodash";
import { Column, Row } from "react-table";

import { EligibilityValueFormatter } from "@/app/components/eligibility-value-formatter";
import { LocationValueWrapper } from "@/app/components/location-value-wrapper/location-value-wrapper";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { AllRecOptions } from "@/app/hooks/use-all-rec-options";
import { CertificationAndEliglibilityType } from "@/app/lib/format-attributes";
import { getFuelSourceIcon } from "@/app/lib/get-fuel-source-icon";
import { decodeAndParseHtmlContent } from "@/app/lib/html-encoder-decoder";
import { TradePosition } from "@/app/types/generated/graphql";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { TradeContractAssetsTableData } from "../../forward-trade-recs-assign-modal.types";

export const getRecsAssignedColumns = (
  t: TFunction,
  followErcotNamingConvention?: boolean,
  options?: {
    includeRegistryName?: boolean;
    recOptionsByRegistryCode?: AllRecOptions["recOptionsByRegistryCode"];
  },
) => {
  const tableColumns: Column<TradeContractAssetsTableData>[] = [
    {
      Header: t("Vintage"),
      disableSortBy: true,
      accessor: "vintages",
      Cell: ({ value }: { value: string }) => {
        return <TableValueWrapper value={value} csv title="Vintages" containerSx={{ minWidth: 80 }} />;
      },
    },
    {
      Header: t(followErcotNamingConvention ? "FIN" : "Project"),
      accessor: "projects",
      Cell: ({ value }: { value: string | string[] }) => {
        const projectValue = value || value?.length ? value : "-";
        return <TableValueWrapper value={projectValue} csv title={"Projects"} containerSx={{ minWidth: 100 }} />;
      },
      disableSortBy: true,
    },
    {
      Header: t("Location"),
      accessor: "location",
      Cell: ({ row }: { row: Row<TradeContractAssetsTableData> }) => {
        const { locations, registryCode } = row.original;
        return (
          <LocationValueWrapper
            value={locations ?? []}
            containerSx={{ minWidth: 110 }}
            recOptions={options?.recOptionsByRegistryCode?.[registryCode as TraceXSupportedRegistries]}
          />
        );
      },
      disableSortBy: true,
    },
    {
      Header: t(followErcotNamingConvention ? "Certification" : "Eligibility"),
      accessor: "certificationsAndEligibilities",
      disableSortBy: true,
      Cell: ({ value }: { value: CertificationAndEliglibilityType[] }) => {
        return <EligibilityValueFormatter certificationsAndEligibilities={value} position={TradePosition.Ask} />;
      },
    },
    {
      Header: t(followErcotNamingConvention ? "Technology Type" : "Fuel Type"),
      Cell: ({ row }: { row: Row<TradeContractAssetsTableData> }) => {
        const { fuelSources, registryCode } = row.original;
        return (
          <Flex sx={{ color: "textDark" }}>
            {!fuelSources || fuelSources.length < 1 ? (
              "-"
            ) : (
              <TableValueWrapper
                getIcon={getFuelSourceIcon}
                title={followErcotNamingConvention ? "Technology Type" : "Fuel Type"}
                value={fuelSources}
                getToolTipText={(key: string) =>
                  options?.recOptionsByRegistryCode?.[
                    registryCode as TraceXSupportedRegistries
                  ]?.fuelSourceOptions.find((x) => x.value === key)?.label ?? ""
                }
              />
            )}
          </Flex>
        );
      },
      disableSortBy: true,
    },
    {
      Header: t("Certificate Type"),
      accessor: "certificateType",
      disableSortBy: true,
      Cell: ({ row }: { row: Row<TradeContractAssetsTableData> }) => {
        const certificateType = row.original?.certificateTypes ?? "-";
        return <TableValueWrapper value={certificateType} />;
      },
    },
    {
      Header: t("Commencement Date (COD)"),
      id: "commencementDate",
      Cell: ({ row }: { row: Row<TradeContractAssetsTableData> }) => {
        const { commencementDate } = row.original.attributes;
        return <TableValueWrapper value={commencementDate ?? "-"} />;
      },
      disableSortBy: true,
    },
    ...(options?.includeRegistryName
      ? [
          {
            Header: t("Registry"),
            Cell: ({ row }: { row: Row<TradeContractAssetsTableData> }) => (
              <TableValueWrapper containerSx={{ minWidth: 60 }} value={row.original.registryDisplayName} />
            ),
            disableSortBy: true,
          },
        ]
      : []),
    {
      Header: t("Status"),
      accessor: "status",
      disableSortBy: true,
      Cell: ({ value, row }: { value: string; row: Row<TradeContractAssetsTableData> }) => {
        const { statusMessage, id } = row.original;
        return (
          <Flex sx={{ color: "textDark", alignItems: "center", whiteSpace: "nowrap" }}>
            {`${t(startCase(toLower(value)))}`}
            {statusMessage && (
              <Tooltip
                id={`recs-${id}-status-tooltip`}
                content={<Text>{decodeAndParseHtmlContent(t(statusMessage))}</Text>}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: 16,
                  height: 16,
                  marginTop: 1,
                }}
              />
            )}
          </Flex>
        );
      },
    },
    {
      Header: t("Quantity"),
      accessor: "volume",
      disableSortBy: true,
    },
  ];
  return tableColumns;
};
